/** Authentication state and methods for the whole app. */

import { createContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { post, get, patch } from '../services/api';
import type { AuthResponse, AuthUser } from '../types/api';

export type AuthActionResult = { success: true } | { success: false; error: string };
export type AuthUserResult = { success: true; user: AuthUser } | { success: false; error: string };

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  login: (username: string, password: string, remember?: boolean) => Promise<AuthUserResult>;
  logout: () => Promise<AuthActionResult>;
  updateProfile: (updates: Partial<AuthUser>) => Promise<AuthUserResult>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthActionResult>;
  checkAuth: () => Promise<void>;
  setLanguage: (language: string) => Promise<AuthActionResult>;
}

const getErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): ReactNode => {
  const { t } = useTranslation(['auth']);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // An explicit language pick made while logged out, re-applied and persisted
  // once login succeeds — otherwise the saved profile language (below) would
  // silently overwrite it. Only ever set via setLanguage(), never detection.
  const pendingLanguageRef = useRef<string | null>(null);

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await get<AuthResponse>('/auth/me');
      setUser(response.user);
      if (response.user.language) {
        void i18n.changeLanguage(response.user.language);
      }
    } catch {
      setUser(null); // Not authenticated or session expired.
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (
    username: string,
    password: string,
    remember = false
  ): Promise<AuthUserResult> => {
    try {
      setLoading(true);
      setError(null);

      const response = await post<AuthResponse>('/auth/login', { username, password, remember });
      setUser(response.user);

      const pendingLanguage = pendingLanguageRef.current;
      pendingLanguageRef.current = null;
      if (pendingLanguage) {
        // Wins over the saved profile language and gets persisted now that
        // we're authenticated. A failed PATCH must not fail the login.
        void i18n.changeLanguage(pendingLanguage);
        if (pendingLanguage !== response.user.language) {
          try {
            const patched = await patch<AuthResponse>('/auth/me', { language: pendingLanguage });
            setUser(patched.user);
          } catch {
            // Non-fatal: login already succeeded.
          }
        }
      } else if (response.user.language) {
        void i18n.changeLanguage(response.user.language);
      }

      return { success: true, user: response.user };
    } catch (err) {
      const message = getErrorMessage(err, t('auth:loginFailed'));
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<AuthActionResult> => {
    try {
      setLoading(true);
      setError(null);

      await post('/auth/logout');
      setUser(null);

      return { success: true };
    } catch (err) {
      setUser(null); // Clear even if the logout request itself failed.
      console.error('Logout error:', err);
      return { success: false, error: getErrorMessage(err, t('auth:logoutFailed')) };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates: Partial<AuthUser>): Promise<AuthUserResult> => {
    try {
      setLoading(true);
      setError(null);

      const response = await post<AuthResponse>('/auth/profile', updates);
      setUser(response.user);

      return { success: true, user: response.user };
    } catch (err) {
      const message = getErrorMessage(err, t('auth:profileUpdateFailed'));
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<AuthActionResult> => {
    try {
      setLoading(true);
      setError(null);

      await post('/auth/change-password', { currentPassword, newPassword });

      return { success: true };
    } catch (err) {
      const message = getErrorMessage(err, t('auth:passwordChangeFailed'));
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  /** Always updates i18next so it works pre-login too; persists to the
   * profile when logged in, else remembers the pick for login() to apply. */
  const setLanguage = async (language: string): Promise<AuthActionResult> => {
    void i18n.changeLanguage(language);

    if (!user) {
      pendingLanguageRef.current = language;
      return { success: true };
    }

    try {
      const response = await patch<AuthResponse>('/auth/me', { language });
      setUser(response.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: getErrorMessage(err, t('auth:languageSaveFailed')) };
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setError(t('auth:sessionExpired'));
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    logout,
    updateProfile,
    changePassword,
    checkAuth,
    setLanguage,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
